/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";
import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { External } from "../types";

/**
 * Bitrix24 mode (configuration.config.crmSource = 'bitrix', crm/db/18_external_ids.sql):
 * the client keeps their Bitrix24, the CRM copies it every 10 minutes and
 * records from it (origin 'b24') are read-only here, for everyone. The
 * database refuses the writes; these helpers only hide what would be refused.
 */
export const useBitrixMode = () =>
  useConfigurationContext().crmSource === "bitrix";

/** the CRM menu (leads, deals, clients, tasks) is on; off for a client who keeps their own CRM */
export const useCrmModule = () =>
  useConfigurationContext().modules?.crm !== false;

export const isFromBitrix = (record?: External | null) =>
  record?.origin === "b24";

/** a Bitrix24 record in Bitrix24 mode: no edit, no delete, no stage move */
export const useReadOnly = (record?: External | null) => {
  const bitrix = useBitrixMode();
  return bitrix && isFromBitrix(record);
};

/** hides its children on a read-only Bitrix24 record */
export const NotFromBitrix = ({
  record,
  children,
}: {
  record?: External | null;
  children: ReactNode;
}) => (useReadOnly(record) ? null : <>{children}</>);

/** leads, deals, contacts and companies are made in Bitrix24 in Bitrix24 mode */
export const CanCreateHere = ({ children }: { children: ReactNode }) =>
  useBitrixMode() ? null : <>{children}</>;

/** the small «из Битрикса» mark, in Bitrix24 mode only */
export const FromBitrixBadge = ({
  record,
  className,
}: {
  record?: External | null;
  className?: string;
}) => {
  const translate = useTranslate();
  if (!useReadOnly(record)) return null;
  return (
    <Badge
      variant="outline"
      className={cn("font-normal text-muted-foreground shrink-0", className)}
      title={translate("crm.bitrix.badge_hint")}
    >
      {translate("crm.bitrix.badge")}
    </Badge>
  );
};
